import * as React from 'react';
import { Lock, PlugZap, RefreshCw, Timer, WifiOff } from 'lucide-react';

import { Button } from '../primitives/button';
import { DegradedPanel, RetryWithCorrelation } from './degraded-state-panel';
import { assessFreshness, type FreshnessOptions } from './degraded-state-core';

/**
 * The four surfaces that annotate or replace a view when the transport, the
 * authorization decision, or the freshness of the data is the problem —
 * forbidden, stale source, offline, and named-downstream-unavailable.
 *
 * Every one of them is deliberately NON-committal about domain meaning: none
 * ever renders a conclusion like "no findings".
 */

// ─── Forbidden (no object leak) ───────────────────────────────────────

export interface ForbiddenStateProps {
  /**
   * Generic noun for what was refused — "record", "report", "site".
   * Deliberately NOT an identifier: naming the object confirms it exists
   * to a caller who is not permitted to know that.
   */
  subject?: string;
  /** The permission/role the caller would need, when safe to disclose. */
  requiredPermission?: string;
  /** Correlation id for an access-review request. */
  correlationId?: string;
  /** Optional "request access" action. */
  onRequestAccess?: () => void;
  className?: string;
}

/**
 * Authorization-denied surface that leaks NOTHING about the object.
 *
 * The copy is identical whether the record exists, does not exist, or
 * belongs to another tenant — so a probing caller cannot use the response
 * to enumerate. It also never renders the server's own 403 detail, which
 * routinely names the record ("Batch B-1042 is export-controlled").
 */
export function ForbiddenState({
  subject = 'this',
  requiredPermission,
  correlationId,
  onRequestAccess,
  className,
}: ForbiddenStateProps) {
  return (
    <DegradedPanel
      tone="danger"
      icon={<Lock className="h-4 w-4" />}
      title="Access denied"
      testId="degraded-forbidden"
      className={className}
      footer={
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {onRequestAccess && (
            <Button type="button" variant="outline" size="sm" onClick={onRequestAccess}>
              Request access
            </Button>
          )}
          {correlationId && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>Reference</span>
              <code
                className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground"
                data-testid="degraded-correlation-id"
              >
                {correlationId}
              </code>
            </span>
          )}
        </div>
      }
    >
      <p>
        Your role does not grant access to {subject}. Nothing about it — including
        whether it exists — can be shown here.
      </p>
      {requiredPermission && (
        <p className="mt-1 text-xs">
          Required permission: <code className="font-mono">{requiredPermission}</code>
        </p>
      )}
    </DegradedPanel>
  );
}

// ─── Stale source ─────────────────────────────────────────────────────

export interface StaleSourceBannerProps {
  /** When the underlying source last produced this data. */
  asOf: Date | string | number | null | undefined;
  /** Human name of the source — "Historian", "SAP mirror", "Edge gateway". */
  source?: string;
  /** Freshness thresholds + injectable clock. */
  freshness?: FreshnessOptions;
  onRefresh?: () => void;
  /** Render even when the data is fresh (as a quiet "as of" line). */
  alwaysShow?: boolean;
  className?: string;
}

/**
 * Annotates data that IS shown but came from a source past its freshness
 * horizon. Deliberately additive: stale data is still data, and hiding it
 * mid-shift is worse than labelling it.
 */
export function StaleSourceBanner({
  asOf,
  source,
  freshness,
  onRefresh,
  alwaysShow,
  className,
}: StaleSourceBannerProps) {
  const assessment = assessFreshness(asOf, freshness);
  if (!assessment.stale && !alwaysShow) return null;

  const ageLabel = Number.isFinite(assessment.ageMs)
    ? formatAge(assessment.ageMs)
    : 'an unknown time';

  return (
    <DegradedPanel
      tone={assessment.stale ? 'warning' : 'info'}
      icon={<Timer className="h-4 w-4" />}
      title={assessment.stale ? 'Showing stale data' : 'Data is current'}
      testId="degraded-stale-source"
      assertive={assessment.stale}
      className={className}
      footer={
        onRefresh ? (
          <div className="mt-3">
            <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Refresh
            </Button>
          </div>
        ) : undefined
      }
    >
      <p data-testid="degraded-stale-detail">
        {source ? `${source} last updated ` : 'Last updated '}
        {ageLabel} ago. Values may have changed since. Do not use them to sign off
        without refreshing.
      </p>
    </DegradedPanel>
  );
}

function formatAge(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

// ─── Offline ──────────────────────────────────────────────────────────

export interface OfflineBannerProps {
  /** Pass your own connectivity signal; defaults to the browser's. */
  online?: boolean;
  /** Description of what still works while offline. */
  offlineCapability?: string;
  onRetry?: () => void;
  className?: string;
}

/** Subscribe to browser connectivity. Returns `true` when unknown (SSR). */
export function useOnlineStatus(): boolean {
  const subscribe = React.useCallback((onChange: () => void) => {
    globalThis.addEventListener?.('online', onChange);
    globalThis.addEventListener?.('offline', onChange);
    return () => {
      globalThis.removeEventListener?.('online', onChange);
      globalThis.removeEventListener?.('offline', onChange);
    };
  }, []);
  const getSnapshot = React.useCallback(() => {
    const nav = globalThis.navigator;
    return nav ? nav.onLine !== false : true;
  }, []);
  return React.useSyncExternalStore(subscribe, getSnapshot, () => true);
}

export function OfflineBanner({
  online,
  offlineCapability,
  onRetry,
  className,
}: OfflineBannerProps) {
  const detected = useOnlineStatus();
  const isOnline = online ?? detected;
  if (isOnline) return null;

  return (
    <DegradedPanel
      tone="warning"
      icon={<WifiOff className="h-4 w-4" />}
      title="You are offline"
      testId="degraded-offline"
      assertive
      className={className}
      footer={<RetryWithCorrelation onRetry={onRetry} retryLabel="Try again" />}
    >
      <p>
        {offlineCapability
          ? `${offlineCapability} Anything not listed here is unavailable until the connection returns.`
          : 'Nothing on this screen can be trusted as current, and no change can be submitted, until the connection returns.'}
      </p>
    </DegradedPanel>
  );
}

// ─── Downstream unavailable ───────────────────────────────────────────

export interface DownstreamUnavailableStateProps {
  /** The dependency that is down — "Historian", "Tax provider", "ERP". */
  dependency: string;
  /** What the operator can still do without it. */
  fallbackGuidance?: string;
  correlationId?: string;
  onRetry?: () => void;
  className?: string;
}

/**
 * A named upstream is down. Critically this is NOT rendered as an empty
 * result: "the tax provider is unreachable" and "no tax applies" are
 * opposite conclusions, and only one of them is safe to post.
 */
export function DownstreamUnavailableState({
  dependency,
  fallbackGuidance,
  correlationId,
  onRetry,
  className,
}: DownstreamUnavailableStateProps) {
  return (
    <DegradedPanel
      tone="danger"
      icon={<PlugZap className="h-4 w-4" />}
      title={`${dependency} is unavailable`}
      testId="degraded-downstream-unavailable"
      assertive
      className={className}
      footer={<RetryWithCorrelation onRetry={onRetry} correlationId={correlationId} />}
    >
      <p>
        This screen could not reach {dependency}, so the result below is unknown —
        not empty, and not a pass.
        {fallbackGuidance ? ` ${fallbackGuidance}` : ''}
      </p>
    </DegradedPanel>
  );
}
